import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { categoryMetas, type ArticleCategory } from "@/mocks/articles";
import { getLatestArticles, searchArticles } from "@/utils/articles";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") ?? "";
  const [input, setInput] = useState(initialQ);
  const [keyword, setKeyword] = useState(initialQ);
  const [category, setCategory] = useState<ArticleCategory | "all">("all");
  const [loading, setLoading] = useState(false);
  const firstRender = useRef(true);

  useEffect(() => {
    const q = params.get("q") ?? "";
    setInput(q);
    setKeyword(q);
  }, [params]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(t);
  }, [keyword, category]);

  const results = useMemo(() => searchArticles(keyword, category), [keyword, category]);
  const recommendations = useMemo(() => getLatestArticles(4), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setKeyword(input.trim());
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  const filterOptions: { id: ArticleCategory | "all"; name: string }[] = [
    { id: "all", name: "不限" },
    ...categoryMetas.map((c) => ({ id: c.id, name: c.name })),
  ];

  return (
    <SiteLayout>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-6 mb-6">
            全文搜索
          </h1>
          <form
            onSubmit={submit}
            className="flex flex-col sm:flex-row gap-3 max-w-2xl"
          >
            <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-md bg-background-50 border border-background-300 focus-within:border-secondary-400">
              <i className="ri-search-line text-foreground-400" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，搜索标题或正文内容"
                className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 rounded-md bg-secondary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
          <p className="text-xs text-foreground-400 mt-4">本站搜索为本地演示，已预加载全部文章内容</p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-8">
          <div>
            {keyword ? (
              <h2 className="text-sm md:text-base text-foreground-700">
                关键词 <span className="font-semibold text-foreground-950">“{keyword}”</span> 的搜索结果
                <span className="ml-2 text-secondary-700 font-medium">共 {results.length} 条</span>
              </h2>
            ) : (
              <h2 className="text-sm md:text-base text-foreground-700">
                全部文章 <span className="ml-2 text-secondary-700 font-medium">共 {results.length} 条</span>
              </h2>
            )}
          </div>
          <div className="inline-flex p-1 rounded-full bg-background-100 flex-wrap">
            {filterOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setCategory(opt.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  category === opt.id
                    ? "bg-background-50 text-primary-700 shadow-sm"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {opt.name}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-lg border border-background-200 overflow-hidden">
                <div className="w-full aspect-[8/5] bg-background-100 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-full bg-background-100 rounded animate-pulse" />
                  <div className="h-3 w-2/3 bg-background-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-background-300 rounded-lg">
            <i className="ri-search-eye-line text-5xl text-foreground-300" />
            <p className="mt-4 text-foreground-700 font-medium">没有找到相关文章</p>
            <p className="mt-2 text-sm text-foreground-500">
              换个关键词试试，或从下面的推荐开始阅读
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto text-left">
              {recommendations.map((a) => (
                <Link
                  key={a.id}
                  to={`/detail/${a.id}`}
                  className="group flex items-center gap-3 p-3 rounded-lg border border-background-200 hover:border-secondary-300 transition-colors"
                >
                  <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                    <i className="ri-book-open-line" />
                  </span>
                  <span className="text-sm text-foreground-800 line-clamp-2 group-hover:text-secondary-700 transition-colors">
                    {a.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {results.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}